import { Box, FormControlLabel, RadioGroup } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledRadio } from '../../../components/styles/StyledInputs';
import { StyledTypography } from '../../../components/styles/styles';
import {
  getReferralEoiImplicationHint,
  isReferralEoiRowDisabled,
} from './notificationSubscriptionUtils';
import { EventType } from './settingsTypes';

interface NotificationEventRowProps {
  event: EventType;
  allEoiSubscribed: boolean;
  referralAcceptedSubscribed: boolean;
  saving: boolean;
  onToggle: (eventTypeId: string) => void;
}

const NotificationEventRow: React.FC<NotificationEventRowProps> = ({
  event,
  allEoiSubscribed,
  referralAcceptedSubscribed,
  saving,
  onToggle,
}) => {
  const isDisabled = isReferralEoiRowDisabled(
    saving,
    event.id,
    allEoiSubscribed,
    referralAcceptedSubscribed,
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${Palette.tone1.border}`,
        backgroundColor: Palette.tone1.background,
        opacity: isDisabled ? 0.6 : 1,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StyledTypography sx={{ fontWeight: 600 }}>{event.label}</StyledTypography>
          {!event.seen && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                backgroundColor: Palette.default.buttonBg,
                color: 'white',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              New
            </Box>
          )}
          {event.subscribedByImplication && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                backgroundColor: '#6c757d',
                color: 'white',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                fontStyle: 'italic',
              }}
            >
              (Implied)
            </Box>
          )}
        </Box>
        <StyledTypography sx={{ mt: 0.5, fontSize: '0.875rem' }} tone="tone1">
          {event.description}
        </StyledTypography>
        {event.id === 'EOI_SUBMISSION_FROM_REFERRAL' && event.subscribedByImplication && (
          <StyledTypography
            sx={{
              mt: 0.5,
              fontStyle: 'italic',
              fontSize: '0.75rem',
            }}
            tone="tone1"
          >
            {getReferralEoiImplicationHint(allEoiSubscribed, referralAcceptedSubscribed)}
          </StyledTypography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <RadioGroup
          row
          value={event.subscribed ? 'yes' : 'no'}
          onChange={(e) => {
            if (isDisabled) return;
            if (e.target.value === 'yes') {
              if (!event.subscribed) {
                onToggle(event.id);
              }
            } else if (event.subscribed) {
              onToggle(event.id);
            }
          }}
          sx={{ gap: 3 }}
        >
          <FormControlLabel
            value="yes"
            control={<StyledRadio disabled={isDisabled} />}
            label={<StyledTypography sx={{ fontSize: '0.875rem' }}>Yes</StyledTypography>}
            {...(isDisabled ? { disabled: true } : {})}
          />
          <FormControlLabel
            value="no"
            control={<StyledRadio disabled={isDisabled} />}
            label={<StyledTypography sx={{ fontSize: '0.875rem' }}>No</StyledTypography>}
            {...(isDisabled ? { disabled: true } : {})}
          />
        </RadioGroup>
      </Box>
    </Box>
  );
};

export default NotificationEventRow;
